import { List, Set } from "immutable";
import _ from "lodash";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import filterMatchOptions from "../../constants/filterMatchOptions";
import narrativeFilterOptions from "../../constants/narrativeFilterOptions";
import { immutableRecords } from "../../types";
import { appStateSelectors } from "../appState";
import { definitionSelectors } from "../definition";
import dateByAddingSpan from "../definition/calendar/dateFunctions/dateByAddingSpan";
import { timespanToApproximateSeconds } from "../definition/calendar/dateFunctions/timespanFunctions";
import { propertySelectors } from "../definition/properties";
import { referenceSelectors } from "../definition/references";
import { typeSelectors } from "../definition/types";
import { itemSelectors } from "../items";
import { narrativeSelectors } from "../narrative";

const listToLowerCase = terms => terms.map(t => t.toLowerCase());

const valueMatchesTerms = (value, terms, match) => {
  if (terms.count() === 0) {
    return null;
  }

  if (match === filterMatchOptions.ALL) {
    return terms.every(t => value.indexOf(t) !== -1);
  } else if (match === filterMatchOptions.ANY) {
    return terms.some(t => value.indexOf(t) !== -1);
  } else {
    return terms.every(t => value.indexOf(t) === -1);
  }
};

const valueMatchesRange = (value, above, below) => {
  if (!above && !below) {
    return null;
  }
  if (isNaN(value)) {
    return false;
  }
  if (above) {
    if (value < above) {
      return false;
    }
  }
  if (below) {
    if (value > below) {
      return false;
    }
  }
  return true;
};

const itemMatchesLabel = (f, obj) =>
  valueMatchesTerms(obj.label ? obj.label.toLowerCase() : "", listToLowerCase(f.terms), f.match);
const itemMatchesSummary = (f, obj) =>
  valueMatchesTerms(obj.summary ? obj.summary.toLowerCase() : "", listToLowerCase(f.terms), f.match);
// TODO: Is there a reason to call toArray here and not elsewhere?
const itemMatchesTags = (f, obj) =>
  valueMatchesTerms(obj.tags ? listToLowerCase(obj.tags).toArray() : [], listToLowerCase(f.terms), f.match);
const itemMatchesType = (f, obj) => {
  if (f.terms.count() === 0) {
    return null;
  }
  return f.terms.contains(obj.type);
};
const itemMatchesColor = (f, obj) => {
  if (f.terms.count() === 0) {
    return null;
  }
  return f.terms.contains(obj.colorId);
};

const itemMatchesSuper = (f, obj, superAndChildrenById) => {
  if (f.terms.count() === 0) {
    return null;
  }
  // If this item has no parent, return false
  if (!obj.super) {
    return false;
  }
  // If this item's parent is in the filter list, return true
  if (f.terms.contains(obj.super)) {
    return true;
  }
  // Otherwise, call recursively to see if we have a grandparent match
  return itemMatchesSuper(f, superAndChildrenById.get(obj.super), superAndChildrenById);
};

const itemMatchesDate = (f, obj) => {
  if ((!f.above || _.isNil(f.above.timestamp)) && (!f.below || _.isNil(f.below.timestamp))) {
    return null;
  }
  if (f.above && !_.isNil(f.above.timestamp)) {
    if (!obj.endDate || _.isNil(obj.endDate.timestamp) || obj.endDate.timestamp < f.above.timestamp) {
      return false;
    }
  }
  if (f.below && !_.isNil(f.below.timestamp)) {
    if (!obj.startDate || _.isNil(obj.startDate.timestamp) || obj.startDate.timestamp > f.below.timestamp) {
      return false;
    }
  }
  return true;
};

const itemMatchesDuration = (f, obj, calendar) => {
  if (!f.above && !f.below) {
    return null;
  }

  if (!obj.startDate || !obj.startDate.timestamp || !obj.endDate || !obj.endDate.timestamp) {
    // If there is no start date, then we need to compare approximate timespan values
    // instead of exact values.
    if (!_.isNil(obj.duration) && !_.isNil(obj.duration.years)) {
      const objSeconds = timespanToApproximateSeconds(calendar, obj.duration);
      if (f.above) {
        const minSeconds = timespanToApproximateSeconds(calendar, f.above);
        if (minSeconds > objSeconds) {
          return false;
        }
      }
      if (f.below) {
        const maxSeconds = timespanToApproximateSeconds(calendar, f.below);
        if (maxSeconds < objSeconds) {
          return false;
        }
      }
      return true;
    }
    return null;
  }

  if (f.above) {
    if (obj.endDate.timestamp < dateByAddingSpan(calendar, obj.startDate, f.above).timestamp) {
      return false;
    }
  }
  if (f.below) {
    if (obj.endDate.timestamp > dateByAddingSpan(calendar, obj.startDate, f.below).timestamp) {
      return false;
    }
  }
  return true;
};

const itemMatchesNarrative = (f, obj, matchingNarrativeIds) => {
  if (f.narrativeStatus === narrativeFilterOptions.NOT_IN_NARRATIVE) {
    return !matchingNarrativeIds.has(obj.id);
  }
  if (f.narrativeStatus === narrativeFilterOptions.IN_NARRATIVE) {
    return matchingNarrativeIds.has(obj.id);
  }
  return null;
};

const getReferenceFiltersById = (state, id) => getFilterWithId(state, id).relationshipsByReference;
const getFiltersById = state => state.data.filters.byId;

const getItemReferredItemIds = (itemObj, relationshipsById, referenceId) => {
  // otherwise, need to check the references
  const related = itemObj.references
    .map(r => relationshipsById.get(r))
    .filter(r => !referenceId || r.reference === referenceId)
    .map(r => r.object)
    .toArray();
  const invRelated = itemObj.inverseReferences
    .map(r => relationshipsById.get(r))
    .filter(r => !referenceId || r.reference === referenceId)
    .map(r => r.subject)
    .toArray();
  return _.concat(related, invRelated);
};

const itemMatchesReferences = (f, obj, referenceId, relationshipsById) => {
  // If none are selected, let it through...
  if (f.terms.count() === 0) {
    return null;
  }
  if (f.terms.includes(obj.id)) {
    return true;
  }
  // otherwise, need to check the references

  const all = getItemReferredItemIds(obj, relationshipsById, referenceId);
  return valueMatchesTerms(all, f.terms, f.match);
};

const addSupersToItemIds = (itemIds, superAndChildrenById) => {
  let result = Set(itemIds);
  const addSupers = id => {
    const item = superAndChildrenById.get(id);
    result = result.add(id);
    if (item.super) {
      addSupers(item.super);
    }
  };

  result.forEach(id => addSupers(id));
  return result.toList();
};

const itemMatchesReferencesByItemType = (filterMap, obj, relationshipsById, superAndChildrenById) => {
  if (filterMap.every(f => f.terms.count() === 0)) {
    return null;
  }
  const objReferredItemIds = getItemReferredItemIds(obj, relationshipsById);
  // Should check the referred items' parents
  const allReferredItemIds = addSupersToItemIds(objReferredItemIds, superAndChildrenById);
  return filterMap
    .map(f => {
      return f.terms.includes(obj.id) || valueMatchesTerms(allReferredItemIds, f.terms, f.match);
    })
    .toList()
    .toArray();
};

const itemMatchesReferencesInMap = (filterMap, obj, relationshipsById) => {
  return filterMap
    .map((f, refId) => itemMatchesReferences(f, obj, refId, relationshipsById))
    .toList()
    .toArray();
};

const itemMatchesTextPropertiesInMap = (filterMap, obj) =>
  filterMap
    .map((f, propId) =>
      valueMatchesTerms((obj.getIn(["propertyValues", propId]) || "").toLowerCase(), listToLowerCase(f.terms), f.match)
    )
    .toList()
    .toArray();

const itemMatchesNumericPropertiesInMap = (filterMap, obj) => {
  return filterMap
    .map((f, propId) => {
      const v = Number(obj.propertyValues.get(propId));
      return valueMatchesRange(v, f.above, f.below);
    })
    .toList()
    .toArray();
};

const itemMatchesBoolPropertiesInMap = (filterMap, obj) => {
  return filterMap
    .map((f, propId) => {
      const v = Boolean(obj.propertyValues.get(propId));
      if (_.isNil(f.expected)) {
        return null;
      }
      return f.expected === v;
    })
    .toList()
    .toArray();
};

/**
 * @param {FilterRecord} filter           [0]
 * @param {ItemDisplayRecord} displayItem [1]
 * @param {Map} superAndChildrenById      [2]
 * @param {Set} matchingNarrativeIds      [3]
 * @param {Map} relationshipsById         [4]
 * @param {CalendarRecord} calendar       [5]
 * @param {string} filterId               [6]
 */
const itemMatchesFilter = createCachedSelector(
  // just select everything as inputs
  (...params) => params[0],
  (...params) => params[1],
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4],
  (...params) => params[5],
  // calculate result
  (filter, displayItem, superAndChildrenById, matchingNarrativeIds, relationshipsById, calendar) => {
    const results = List(
      _.flatten([
        itemMatchesLabel(filter.label, displayItem),
        itemMatchesSummary(filter.summary, displayItem),
        itemMatchesTags(filter.tags, displayItem),
        itemMatchesType(filter.type, displayItem),
        itemMatchesColor(filter.color, displayItem),
        itemMatchesSuper(filter.super, displayItem, superAndChildrenById),
        itemMatchesDate(filter.date, displayItem),
        itemMatchesDuration(filter.duration, displayItem, calendar),
        itemMatchesNarrative(filter.narrative, displayItem, matchingNarrativeIds),
        itemMatchesReferencesByItemType(
          filter.relationshipsByType,
          displayItem,
          relationshipsById,
          superAndChildrenById
        ),
        itemMatchesReferencesInMap(filter.relationshipsByReference, displayItem, relationshipsById),
        itemMatchesTextPropertiesInMap(filter.textProperties, displayItem),
        itemMatchesNumericPropertiesInMap(filter.numericProperties, displayItem),
        itemMatchesBoolPropertiesInMap(filter.boolProperties, displayItem)
      ])
    );
    let value = false;
    if (filter.matchCriteria === filterMatchOptions.ALL) {
      value = results.every(r => _.isNil(r) || r);
    } else {
      value = results.some(Boolean);
    }

    return value;
  }
)((...params) => params[6]);

/**
 * @param {FilterRecord} filter           [0]
 * @param {List} itemIds                  [1]
 * @param {Map} displayItemsById          [2]
 * @param {Map} superAndChildrenById      [3]
 * @param {TreeRecord} narrative          [4]
 * @param {Map} relationshipsById         [5]
 * @param {CalendarRecord} calendar       [6]
 * @param {string} filterId               [7]
 */
const itemIdsMatchingFilter = (
  filter,
  itemIds,
  displayItemsById,
  superAndChildrenById,
  narrative,
  relationshipsById,
  calendar,
  filterId
) => {
  if (!filter || !filterIsActive(filter)) {
    return itemIds;
  }

  const matchingNarrativeIds = narrativeSelectors.getNarrativeIdsBetweenIdsAsSet(
    narrative,
    filter.narrative.from,
    filter.narrative.to,
    filterId
  );

  return itemIds.filter(id => {
    return itemMatchesFilter(
      filter,
      displayItemsById.get(id),
      superAndChildrenById,
      matchingNarrativeIds,
      relationshipsById,
      calendar,
      filterId
    );
  });
};

const getFilterWithIdFromFilters = (filtersById, filterId) =>
  filtersById.has(filterId) ? filtersById.get(filterId) : immutableRecords.FilterRecord();

const getFilterWithId = (state, filterId) => getFilterWithIdFromFilters(state.data.filters.byId, filterId);
const getFiltersAsList = state => state.data.filters.allIds.map(id => getFilterWithId(state, id));

const filterIsActive = filter => {
  return Boolean(
    filter &&
      (filter.label.terms.count() > 0 ||
        filter.summary.terms.count() > 0 ||
        filter.tags.terms.count() > 0 ||
        filter.type.terms.count() > 0 ||
        filter.color.terms.count() > 0 ||
        filter.super.terms.count() > 0 ||
        filter.date.above ||
        filter.date.below ||
        filter.duration.above ||
        filter.duration.below ||
        filter.narrative.narrativeStatus ||
        filter.relationshipsByType.some(r => r.terms.count() > 0) ||
        filter.relationshipsByReference.some(r => r.terms.count() > 0) ||
        filter.textProperties.some(p => p.terms.count() > 0) ||
        filter.numericProperties.some(p => p.above || p.below) ||
        filter.boolProperties.some(p => !_.isNil(p.expected)))
  );
};

const isFilterWithIdActive = (state, filterId) => filterIsActive(state.data.filters.byId.get(filterId));

const countActiveFilterGroups = (state, filterId) => {
  const filter = state.data.filters.byId.get(filterId);
  let count = 0;
  if (!filter) {
    return count;
  }

  for (let [, value] of filter) {
    if (value.above || value.below || value.narrativeStatus || (value.terms && value.terms.size)) {
      count++;
    } else {
      for (let [, v] of value) {
        if (v && v.terms && v.terms.size) {
          count++;
        }
      }
    }
  }

  return count;
};

/**
 * @param {TimelineRecord} state
 * @param {string} filterId
 */
const collapsedGroups = createSelector(
  state => typeSelectors.getOrderedTypes(state),
  state => referenceSelectors.getOrderedReferences(state),
  state => propertySelectors.getOrderedProperties(state),
  state => itemSelectors.getItemIdsByType(state),
  (state, filterId) => appStateSelectors.getExpandedFilterGroups(state, filterId),
  state => definitionSelectors.isNarrativeAllowed(state),
  state => appStateSelectors.getExposedFilterTypes(state),
  (types, references, properties, itemIdsByType, expandedGroups, isNarrativeAllowed, exposedFilterTypes) => {
    // Get all the filter groups we want to list
    let filters = List([
      { value: "color", label: "Color" },
      { value: "type", label: "Type" },
      { value: "tags", label: "Tags" },
      { value: "date", label: "Date" },
      { value: "duration", label: "Duration" },
      { value: "label", label: "Label" },
      { value: "summary", label: "Summary" }
    ]);

    if (isNarrativeAllowed) {
      filters = filters.push({ value: "narrative", label: "Narrative" });
    }

    types.map(type => {
      const isReferenceTarget = references.filter(reference => reference.types.includes(type.id)).count() > 0;
      const itemsOfTypeExist = itemIdsByType.get(type.id).count() > 0;

      if (isReferenceTarget && itemsOfTypeExist) {
        filters = filters.concat({ value: type.id, label: type.label });
      }
    });

    references
      .filter(
        r =>
          r.types.count() > 1 ||
          referenceSelectors.getReferencesWithTypeFromReferences(references, r.types.get(0)).count() > 1
      )
      .map(reference => {
        const referenceHasTargetItems = reference.types.filter(type => itemIdsByType.get(type).count() > 0).count() > 0;
        if (referenceHasTargetItems) {
          filters = filters.concat({ value: reference.id, label: reference.label });
        }
      });

    properties.map(p => {
      filters = filters.concat({ value: p.id, label: p.label });
    });

    // Remove filter groups that are already expanded
    filters = filters.filter(filter => !expandedGroups.contains(filter.value));

    // Remove filter groups that are exposed by default
    filters = filters.filter(filter => !exposedFilterTypes.includes(filter.value));

    return filters;
  }
);

const getAllFilterGroups = createSelector(
  state => typeSelectors.getOrderedTypes(state),
  state => referenceSelectors.getOrderedReferences(state),
  state => propertySelectors.getOrderedProperties(state),
  state => itemSelectors.getItemIdsByType(state),
  (state, filterId) => appStateSelectors.getExpandedFilterGroups(state, filterId),
  state => definitionSelectors.isNarrativeAllowed(state),
  (types, references, properties, itemIdsByType, expandedGroups, isNarrativeAllowed) => {
    // Get all the filter groups we want to list
    let filters = List([
      { value: "color", label: "Color" },
      { value: "type", label: "Type" },
      { value: "tags", label: "Tags" },
      { value: "date", label: "Date" },
      { value: "duration", label: "Duration" },
      { value: "label", label: "Label" },
      { value: "summary", label: "Summary" }
    ]);

    if (isNarrativeAllowed) {
      filters = filters.push({ value: "narrative", label: "Narrative" });
    }

    types.map(type => {
      const isReferenceTarget = references.filter(reference => reference.types.includes(type.id)).count() > 0;
      const itemsOfTypeExist = itemIdsByType.get(type.id).count() > 0;

      if (isReferenceTarget && itemsOfTypeExist) {
        filters = filters.concat({ value: type.id, label: type.label });
      }
    });

    references
      .filter(
        r =>
          r.types.count() > 1 ||
          referenceSelectors.getReferencesWithTypeFromReferences(references, r.types.get(0)).count() > 1
      )
      .map(reference => {
        const referenceHasTargetItems = reference.types.filter(type => itemIdsByType.get(type).count() > 0).count() > 0;
        if (referenceHasTargetItems) {
          filters = filters.concat({ value: reference.id, label: reference.label });
        }
      });

    properties.map(p => {
      filters = filters.concat({ value: p.id, label: p.label });
    });

    return filters;
  }
);

export default {
  itemMatchesFilter,
  itemIdsMatchingFilter,
  getFilterWithId,
  getFilterWithIdFromFilters,
  isFilterWithIdActive,
  getFiltersAsList,
  getReferenceFiltersById,
  getFiltersById,
  countActiveFilterGroups,
  collapsedGroups,
  getAllFilterGroups
};
